/**
 * Date formatters and shared helpers for the reservations feature.
 *
 * Date formatting itself is delegated to the app-wide `src/lib/date-format.ts`
 * helpers so every screen renders dates in the same format — see that module
 * for the canonical formats. `formatPolishDate`/`formatPolishDateTime` are
 * kept here as thin re-exports so existing call sites in this feature don't
 * need to change their import path.
 *
 * mapBaseReservationFields: shared mapper for fields common to both ReservationListItem
 * and UnitReservationRow. Callers supply the translated statusLabel (via t()).
 * `isQueued` is derived from the raw contract `queuePosition` field (kept only
 * for the ReservationStatusBadge HARD-styling gate) — the position/total value
 * itself is no longer mapped or rendered anywhere.
 *
 * makeDialogLabels: builds the full ReservationDialogLabels object from a t() function,
 * shared between /reservations and /media-units/$id routes.
 */
import type { ReservationListItem as ContractItem } from '@ooh/contracts';
import type { ReservationDialogLabels } from '../components/reservation-action-dialogs';
import { formatDisplayDate, formatDisplayDateTime } from '@ooh/ui/lib/date-format';

/**
 * Formats a Date (or ISO string) as a short display date+time, e.g. "1 sie 2026, 14:30".
 */
export const formatPolishDateTime = formatDisplayDateTime;

/**
 * Formats a Date (or ISO string) as a short display date, e.g. "1 sie 2026".
 */
export const formatPolishDate = formatDisplayDate;

// ─── Date field coercion ──────────────────────────────────────

/**
 * Coerces a date field (Date object or ISO string) to a YYYY-MM-DD string.
 * Used by route mappers to normalise API responses where z.coerce.date()
 * has already converted string values to Date objects.
 */
export function coerceDateField(value: string | Date): string {
  return value instanceof Date ? value.toISOString().slice(0, 10) : String(value).slice(0, 10);
}

// ─── Reservation focus URL param helpers ─────────────────────

/** Prefix used in the `focus` URL query param to identify a reservation. */
export const RESERVATION_FOCUS_PREFIX = 'reservation:';

/**
 * Builds the `focus` URL param value for a reservation row.
 * e.g. buildReservationFocusParam(42) → 'reservation:42'
 */
export function buildReservationFocusParam(id: number | string): string {
  return `${RESERVATION_FOCUS_PREFIX}${String(id)}`;
}

/**
 * Parses a `focus` URL param value to extract the reservation id string,
 * or returns null if the value does not match the reservation prefix.
 * e.g. parseReservationFocusParam('reservation:42') → '42'
 */
export function parseReservationFocusParam(focus: string): string | null {
  if (focus.startsWith(RESERVATION_FOCUS_PREFIX)) {
    return focus.slice(RESERVATION_FOCUS_PREFIX.length);
  }
  return null;
}

// ─── Reservation status label (i18n bridge) ───────────────────

/**
 * Returns a translated Polish label for the given reservation status.
 * Callers must pass the t() function from the 'reservations' i18n namespace.
 * This keeps formatters.ts free of React hooks while supporting i18n labels.
 *
 * @param status - Raw status enum value from the API
 * @param t - Translation function from useTranslation('reservations')
 */
export function formatReservationStatus(status: string, t: (key: string) => string): string {
  return t(`status.${status}`);
}

// ─── Reservation source label (i18n bridge) ───────────────────

/**
 * Returns a translated Polish label for the given reservation source enum
 * value (e.g. 'xlsx_import' -> 'Import XLSX'). Falls back to the raw value
 * via i18next's `defaultValue` when an unrecognised source is encountered
 * (e.g. a future backend enum addition not yet translated), so the UI never
 * shows a raw i18next key.
 *
 * @param source - Raw source enum value from the API
 * @param t - Translation function from useTranslation('reservations')
 */
export function formatReservationSource(
  source: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  return t(`source.${source}`, { defaultValue: source });
}

// ─── Audit action label (i18n bridge) ─────────────────────────

/**
 * Returns a translated Polish label for the given audit-log action enum
 * value (e.g. 'reservation.created' -> 'Utworzono rezerwację'). Falls back to
 * the raw value via i18next's `defaultValue` when an unrecognised action is
 * encountered (e.g. a future backend action not yet translated), so the
 * "Pełna historia zdarzeń" timeline never shows a raw i18next key.
 *
 * @param action - Raw action enum value from the API (e.g. 'reservation.created')
 * @param t - Translation function from useTranslation('reservations')
 */
export function formatAuditAction(
  action: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  return t(`auditActions.${action}`, { defaultValue: action });
}

// ─── Shared base reservation mapper ──────────────────────────

/**
 * Fields shared between ReservationListItem (view) and UnitReservationRow (unit tab).
 * Both route mappers call this to avoid duplicating the common field mapping logic.
 */
export interface BaseReservationFields {
  id: number;
  status: 'SOFT_RESERVATION' | 'HARD_RESERVATION' | 'SOLD' | 'OPTION';
  isQueued: boolean;
  dateFrom: string;
  dateTo: string;
  holderUserId: number;
  holderUserName: string;
  offerId: number | null;
  offerName: string | null;
  offerHref: string | null;
  source: string;
  sourceLabel: string;
  statusLabel: string;
  /**
   * ISO instant string, or null. Raw TTL only — no longer used for the
   * "Edytuj termin wygaśnięcia" eligibility gate (see `effectiveExpiresAt`
   * below); kept for any future raw-TTL display need.
   */
  ttlExpiresAt: string | null;
  /**
   * ISO instant string, or null. Server-authoritative effective release
   * boundary (CU-86cb7qgc9 follow-up, commit 8993b4ac2): `ttlExpiresAt` for
   * `SOFT_RESERVATION`, `GREATEST(ttlExpiresAt, signingDeadlineAt)` for
   * `HARD_RESERVATION`, null for `OPTION`/`SOLD` — matches the
   * reservation-engine D-1 definition. `isDeadlineEditEligible` (below) uses
   * this instead of `ttlExpiresAt` so a HARD row whose TTL elapsed but whose
   * signing deadline is still open still shows the action.
   */
  effectiveExpiresAt: string | null;
}

/**
 * Maps the common fields from a ContractItem to BaseReservationFields.
 * Unique fields (e.g. mediaUnitId, unitHref, targetVisibilityDays) are added
 * by each route's own mapper on top of this result.
 *
 * @param item - Raw API response item
 * @param statusLabel - Pre-translated Polish status label (from t('status.' + item.status))
 * @param sourceLabel - Source label; callers may pass the raw source value until a lookup is added
 */
export function mapBaseReservationFields(
  item: ContractItem,
  statusLabel: string,
  sourceLabel: string,
): BaseReservationFields {
  const dateFrom = coerceDateField(item.dateFrom);
  const dateTo = coerceDateField(item.dateTo);
  return {
    id: item.id,
    status: item.status,
    isQueued: item.queuePosition !== null,
    dateFrom,
    dateTo,
    holderUserId: item.holderUserId,
    holderUserName: item.holderUserName,
    offerId: item.offerId,
    offerName: item.offerName,
    offerHref: item.offerId !== null ? `/offers/${String(item.offerId)}` : null,
    source: item.source,
    sourceLabel,
    statusLabel,
    ttlExpiresAt:
      item.ttlExpiresAt instanceof Date ? item.ttlExpiresAt.toISOString() : item.ttlExpiresAt,
    effectiveExpiresAt:
      item.effectiveExpiresAt instanceof Date
        ? item.effectiveExpiresAt.toISOString()
        : item.effectiveExpiresAt,
  };
}

// ─── Deadline-edit row-action eligibility (CU-86cb7qgc9) ──────

/**
 * UX-affordance-only eligibility check for the "Edytuj termin wygaśnięcia"
 * row action: SOFT/HARD status and a not-yet-elapsed `effectiveExpiresAt`.
 * The server (`reservation.extend` guard + the locked re-read in
 * `editReservationExpiryDeadline`) remains the sole enforcement point — this
 * only decides whether to render the menu item at all (hide, never disable).
 *
 * CU-86cb7qgc9 follow-up (commit 8993b4ac2): takes the server-projected
 * `effectiveExpiresAt` (raw TTL for SOFT, `GREATEST(ttl, signingDeadline)`
 * for HARD — see reservation-engine D-1), not a client-side `ttlExpiresAt`
 * recomputation. A HARD_RESERVATION row's true boundary can be LATER than
 * its `ttlExpiresAt` while `signing_deadline_at` is still open; using
 * `ttlExpiresAt` alone would incorrectly hide the action in that window.
 */
export function isDeadlineEditEligible(
  status: 'SOFT_RESERVATION' | 'HARD_RESERVATION' | 'SOLD' | 'OPTION',
  effectiveExpiresAt: string | null,
): boolean {
  if (status !== 'SOFT_RESERVATION' && status !== 'HARD_RESERVATION') return false;
  if (effectiveExpiresAt === null) return true;
  return new Date(effectiveExpiresAt).getTime() > Date.now();
}

// ─── Expiry-deadline column display (reservation-panel spec) ──

export type DeadlineDisplayKind = 'scheduled' | 'expired' | 'none' | 'never';

export interface DeadlineDisplay {
  kind: DeadlineDisplayKind;
  /** Formatted display date via formatDisplayDate — present for 'scheduled'/'expired' only. */
  formattedDate?: string;
}

/**
 * Resolves what the expiry-deadline column (or an equivalent surface, e.g. the
 * availability-calendar cell popover) should show for a reservation, per the
 * reservation-panel spec's "Reservation expiry deadline is visible on every
 * reservation list surface" requirement:
 * - SOLD/OPTION never expire → 'never'
 * - SOFT/HARD with no effectiveExpiresAt → 'none' (an operational gap — must
 *   read differently from 'never', which is a legitimate terminal state)
 * - SOFT/HARD with an effectiveExpiresAt already in the past → 'expired'
 *   (awaiting the release sweep — the most urgent row on the screen)
 * - SOFT/HARD with a future effectiveExpiresAt → 'scheduled'
 *
 * Pure function — callers own how each kind renders (marker text, styling).
 */
export function resolveDeadlineDisplay(
  status: 'SOFT_RESERVATION' | 'HARD_RESERVATION' | 'SOLD' | 'OPTION',
  effectiveExpiresAt: string | null,
): DeadlineDisplay {
  if (status === 'SOLD' || status === 'OPTION') return { kind: 'never' };
  if (effectiveExpiresAt === null) return { kind: 'none' };
  const isPast = new Date(effectiveExpiresAt).getTime() <= Date.now();
  return {
    kind: isPast ? 'expired' : 'scheduled',
    formattedDate: formatDisplayDate(effectiveExpiresAt),
  };
}

// ─── Dialog labels factory ────────────────────────────────────

/**
 * Builds the full ReservationDialogLabels object from a t() function.
 * Shared between /reservations and /media-units/$id to avoid copy-paste drift.
 *
 * @param t - Translation function from useTranslation('reservations')
 */
export function makeDialogLabels(
  t: (key: string, opts?: Record<string, unknown>) => string,
): ReservationDialogLabels {
  return {
    upgradeHardTitle: t('dialogs.upgradeHardTitle'),
    upgradeHardBody: t('dialogs.upgradeHardBody'),
    closeSaleTitle: t('dialogs.closeSaleTitle'),
    closeSaleBody: t('dialogs.closeSaleBody'),
    forceReleaseTitle: t('dialogs.forceReleaseTitle'),
    forceReleaseBody: t('dialogs.forceReleaseBody'),
    confirmLabel: t('dialogs.confirmLabel'),
    cancelBtnLabel: t('dialogs.cancelBtnLabel'),
    cancelTitle: t('dialogs.cancelTitle'),
    cancelBody: (mu, df, dt) => t('cancelBody', { mediaUnitName: mu, dateFrom: df, dateTo: dt }),
    cancelConfirmLabel: t('dialogs.cancelConfirmLabel'),
    cancelWillBeFreeLabel: t('dialogs.cancelWillBeFreeLabel'),
    // Shared with InspectQuickActions — same `actions.releaseModeChoice.*` keys, so the
    // two ReleaseModeChoice call sites never diverge in wording (CU-86cbh03pj parity).
    releaseModeChoiceTitle: t('actions.releaseModeChoice.title'),
    releaseModeChoiceWholeLabel: t('actions.releaseModeChoice.wholeLabel'),
    releaseModeChoiceSelectedLabel: t('actions.releaseModeChoice.selectedLabel'),
    editDatesTitle: t('dialogs.editDatesTitle'),
    dateRangeLabel: t('dialogs.dateRangeLabel'),
    displacementWarningLabel: t('dialogs.displacementWarningLabel'),
    forceCheckboxLabel: t('dialogs.forceCheckboxLabel'),
    saveLabel: t('dialogs.saveLabel'),
    overrideStatusTitle: t('dialogs.overrideStatusTitle'),
    targetStatusLabel: t('dialogs.targetStatusLabel'),
    reasonLabel: t('dialogs.reasonLabel'),
    reasonPlaceholder: t('dialogs.reasonPlaceholder'),
    reasonRequiredLabel: t('dialogs.reasonRequiredLabel'),
    auditNoteLabel: t('dialogs.auditNoteLabel'),
    overrideConfirmLabel: t('dialogs.overrideConfirmLabel'),
    ttlExpiresAtLabel: t('dialogs.ttlExpiresAtLabel'),
    ttlExpiresAtHint: t('dialogs.ttlExpiresAtHint'),
    ttlExpiresAtPlaceholder: t('dialogs.ttlExpiresAtPlaceholder'),
    extendExpiryTitle: t('dialogs.extendExpiryTitle'),
    extendExpiryDescription: t('dialogs.extendExpiryDescription'),
    extendExpiryCurrentLabel: t('dialogs.extendExpiryCurrentLabel'),
    extendExpiryNewLabel: t('dialogs.extendExpiryNewLabel'),
    extendExpiryDatePlaceholder: t('dialogs.extendExpiryDatePlaceholder'),
    extendExpiryReasonLabel: t('dialogs.extendExpiryReasonLabel'),
    extendExpiryReasonPlaceholder: t('dialogs.extendExpiryReasonPlaceholder'),
    extendExpiryReasonRequiredLabel: t('dialogs.extendExpiryReasonRequiredLabel'),
    extendExpirySaveLabel: t('dialogs.extendExpirySaveLabel'),
  };
}
